import React, { useState } from 'react';
import {
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Alert,
  Collapse,
  IconButton,
  InputAdornment,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoTip from '../components/InfoTip';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart,
  Bar,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts';
import moment from 'moment-timezone';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { MultiInputDateRangeField } from '@mui/x-date-pickers-pro/MultiInputDateRangeField';
import { PickersActionBar } from '@mui/x-date-pickers/PickersActionBar';
import TimezonePicker from '../components/TimezonePicker';
import { DataAPIContext, GET_ONE } from '../api';
import config from '../config';

/*
 * AI Metrics tab (AI Training > Metrics).
 *
 * NOTE: this is currently driven by MOCK_METRICS so the design can be reviewed
 * before the backend lands. The component reads a single `metrics` object whose
 * shape matches the eventual `report/ai_metrics` response, so wiring live data
 * is a one-line swap (see DATA_SOURCE below). Numbers below mirror the real
 * suggested-reply report output for City Wonders so the layout is realistic.
 *
 * Design principle: the hero is the *coverage spectrum* ("how much of the AI's
 * draft staff actually used"), not a single ">=95% verbatim" number. The <40%
 * band is decomposed into "staff rewrote" (low reuse) vs. AI-deferred and
 * ops-template (not draft-quality failures). NOTE: low reuse is NOT the same as
 * "AI was wrong" -- staff often reword or add policy on top of a correct answer,
 * so the page deliberately avoids claiming wrongness it can't prove from text
 * overlap alone (size the truly-wrong share with the override audit).
 */
// Live data only in production; staging/dev show the polished sample data (the metrics
// feature is still gated/in-progress, and staging data is sparse). Driven by config.env
// (REACT_APP_SENTRY_ENVIRONMENT per .env.{dev,staging,pro}).
const DATA_SOURCE = config.isProduction ? 'live' : 'mock'; // 'live' = fetch report/ai_metrics; 'mock' = sample data

// Shared band palette: green (kept) -> amber -> red (genuine failure) -> grey
// (not a draft-quality issue: AI punted / staff sent a canned op message).
const BAND = {
  // Auto-sent heads the hero bar: the AI's reply went out untouched, so it sits at
  // the dark end of the "kept" ramp, one step deeper than near-verbatim.
  auto_sent: '#1b5e20',
  near_exact: '#2e7d32',
  light: '#66bb6a',
  moderate: '#aed581',
  // The whole "used" spectrum is ONE green ramp (dark = mostly used -> pale = barely
  // used), mirroring the Suggestion-usage-mix trend. Low reuse reads as "less of the
  // draft survived", NOT as an error -- amber/red here wrongly implied the AI was wrong.
  heavy: '#cfe8b0',
  // Faithful trim: low coverage but the sent text is mostly the AI's words (high
  // precision) -- reuse. Stays in the GREEN "kept" family (a foreign hue in the green
  // ramp reads as a rendering glitch), but a clearer green than the pale bands around
  // it so the thin band is still findable -- and a trim IS more "kept" than the paler
  // "Staff reworded" band next to it, so the stronger green reads correctly.
  trimmed: '#9ccc65',
  override: '#dcedc8',
  ai_deferred: '#bdbdbd',
  ops_template: '#b0bec5',
};

// The coverage-mix trend shows only the "kept" bands as a calm green ramp; the
// <40% band is left unfilled (white space above the stack) so the chart reads as
// "how much was used" without a menacing red/grey block dominating it.
const TREND_BAND = {
  near: '#2e7d32',
  light: '#66bb6a',
  trimmed: '#9ccc65', // stacked between light and moderate, matching the hero bar order
  moderate: '#a5d6a7',
  heavy: '#dcedc8',
};

const MOCK_METRICS = {
  operator: { id: 288, name: 'City Wonders' },
  window: { days: 30, start: '2026-05-13', end: '2026-06-12' },
  modes: {
    auto_populate_suggested_response: true,
    auto_reply_with_ai: true,
    ai_grounding_enabled: true,
    ai_auto_reply_min_confidence: 0.9,
    ai_auto_reply_min_confidence_web_widget: 0.95,
    web_chat_widget_available: true,
  },
  // Per-channel split of the usage figures (web chat widget vs support chat) with the
  // reply-kind breakdown. support = total - web_widget on the backend; the two blocks
  // sum to `usage`. Shipped 2026-09-02 -- days rolled up before then read as support.
  channels: {
    web_widget: {
      suggestions_generated: 1842, auto_sent: 1610, shown_to_staff: 232, dropped: 0,
      auto_send_rate: 0.874, gen_deferred: 61, deferral_rate: 0.033, confidence_median: 0.92,
      by_source: { rag: 829, rule: 402, rule_exact: 611, unknown: 0 },
      // Staff replies after a handoff (CP-8400).
      reuse: {
        pairs_scored: 180, cov_mean: 0.52, sym_mean: 0.47, prec_mean: 0.61,
        segments: { near_exact: 70, light: 10, trimmed: 10, moderate: 20, heavy: 15, genuine_override: 35, ai_deferred: 15, ops_template: 5, language_mismatch: 0 },
      },
    },
    support: {
      suggestions_generated: 11420, auto_sent: 423, shown_to_staff: 10997, dropped: 0,
      auto_send_rate: 0.037, gen_deferred: 1544, deferral_rate: 0.135, confidence_median: 0.83,
      by_source: { rag: 9880, rule: 1160, rule_exact: 0, unknown: 380 },
      reuse: {
        pairs_scored: 5950, cov_mean: 0.43, sym_mean: 0.39, prec_mean: 0.34,
        segments: { near_exact: 1930, light: 190, trimmed: 300, moderate: 380, heavy: 540, genuine_override: 1820, ai_deferred: 470, ops_template: 320, language_mismatch: 0 },
      },
    },
  },
  usage: {
    suggestions_generated: 13262,
    auto_sent: 2033,
    shown_to_staff: 11229,
    dropped: 0,
    auto_send_rate: 0.153,
    deferral_rate: 0.121,
    language_mismatch: 312,
    language_mismatch_rate: 0.048,
    // Window-unique: distinct conversations that got a suggestion / an auto-send.
    conversations_with_suggestion: 4821,
    conversations_with_autosent: 1203,
    // auto_sent minus widget holding messages ("passed to our team") -- the hero bar's segment.
    auto_sent_replies: 1883,
  },
  // CP-8400: estimated staff time saved -- fixed minutes per real auto-sent answer and per
  // AI draft staff used (credits_min); holding messages, repeats and deferrals earn nothing.
  // CP-8400 "handled entirely by AI": AI-answered conversations no person replied in.
  containment: {
    answered: 1420, handled: 1012, handled_pct: 0.713,
    channels: {
      web_widget: { answered: 1180, handled: 905, handled_pct: 0.767 },
      support: { answered: 240, handled: 107, handled_pct: 0.446 },
    },
  },
  // Guest messages: distinct conversations, messages, share outside office hours (or 9am-5pm).
  inbound: {
    conversations: 5240, messages: 21880, after_hours_pct: 0.41, basis: 'office_hours',
    office_hours: [{ days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'], start: '09:00', end: '18:00', timezone: 'Europe/Rome' }],
    timezone: 'Europe/Rome',
    channels: {
      web_widget: {
        conversations: 1310, messages: 3420, after_hours_pct: 0.68,
        outcomes: { ai: 1012, team: 168, email: 54, unanswered: 76 },
      },
      support: { conversations: 3930, messages: 18460, after_hours_pct: 0.36 },
    },
  },
  time_saved: {
    hours_total: 135.4,
    hours_auto: 61.7,
    hours_drafts: 73.7,
    auto_replies: 1850,
    drafts_used: { near: 2501, moderate: 392, heavy: 552 },
    // Each channel's AI replies + drafts; the two add up to the totals above (web chat's drafts
    // match its sample reuse figures, support gets the rest).
    channels: {
      web_widget: { auto_replies: 1490, drafts_used: { near: 90, moderate: 20, heavy: 15 }, hours: 52.4 },
      support: { auto_replies: 360, drafts_used: { near: 2411, moderate: 372, heavy: 537 }, hours: 83.0 },
    },
    excluded: { holding_messages: 150, repeats: 12, deferrals: 21 },
    after_hours_pct: 0.31,
    credits_min: { auto_reply: 2.0, draft_near: 1.5, draft_moderate: 1.0, draft_heavy: 0.5 },
  },
  adoption: {
    pairs_scored: 6130,
    cov_median: 0.43,
    cov_mean: 0.41,
    sym_mean: 0.39,
    prec_mean: 0.34,
    // Headline reuse metrics — each is a subtotal of the spectrum's color zones,
    // so the four together sum to 100% and map 1:1 to the bar below.
    mostly_used_pct: 0.358, // >=85% used (near_exact + light) — "Ready to send"
    partially_used_pct: 0.204, // moderate + heavy + trimmed — "Strong starting point"
    genuine_override_pct: 0.303, // RED: <40% used AND low precision — staff rewrote the substance
    not_applicable_pct: 0.135, // GREY: ai_deferred + ops_template (AI didn't give a usable reply)
    // The hero bar: full coverage spectrum with the <40% band decomposed by
    // reason. Segments sum to ~100% of pairs_scored.
    spectrum: [
      { key: 'near_exact', group: 'used', legend: 'Sent near-verbatim', label: '≥95% used — sent essentially as-is', pct: 0.326, color: BAND.near_exact },
      { key: 'light', group: 'used', legend: 'Light staff edit', label: '85–95% used', pct: 0.032, color: BAND.light },
      // 'trimmed' sits here (not by coverage order) so its saturated green flows between
      // Light and Mild in the dark->pale ramp instead of bumping out among the pale bands.
      { key: 'trimmed', group: 'used', legend: 'Used, shortened', label: 'Staff sent mostly the AI’s own words but cut the draft down: they used it, just shorter', pct: 0.050, color: BAND.trimmed },
      { key: 'moderate', group: 'used', legend: 'Mild staff edit', label: '65–85% used', pct: 0.064, color: BAND.moderate },
      { key: 'heavy', group: 'used', legend: 'Moderate staff edit', label: '40–65% used', pct: 0.090, color: BAND.heavy },
      { key: 'genuine_override', group: 'failed', legend: 'Staff reworded', label: 'Staff kept under 40% of the draft and little of what they sent was the AI’s wording: they wrote their own reply', pct: 0.303, color: BAND.override },
      { key: 'ai_deferred', group: 'na', legend: 'AI deferred', label: '<40% — AI punted ("let me check with my team")', pct: 0.079, color: BAND.ai_deferred },
      { key: 'ops_template', group: 'na', legend: 'Ops template', label: '<40% — staff sent a canned op message / link', pct: 0.056, color: BAND.ops_template },
    ],
    // Decomposition of the <40% bucket (shares of that bucket) for the detail panel.
    override_reasons: [
      { key: 'genuine_override', label: 'Staff rewrote (substance changed — not necessarily AI error)', pct: 0.724, color: BAND.override },
      { key: 'ai_deferred', label: 'AI deferred to staff (said it would follow up)', pct: 0.161, color: BAND.ai_deferred },
      { key: 'ops_template', label: 'Ops template (canned msg / link)', pct: 0.115, color: BAND.ops_template },
    ],
  },
  confidence: {
    median: 0.84,
    // Calibration: does the AI's self-graded confidence predict how much staff
    // actually keep? If mean coverage rises with confidence, the threshold is
    // trustworthy. n = how many suggestions fell in that confidence band.
    calibration: [
      { band: '0.90 – 1.00', conf: 0.95, mean_coverage: 0.58, n: 1980 },
      { band: '0.80 – 0.90', conf: 0.85, mean_coverage: 0.50, n: 820 },
      { band: '0.70 – 0.80', conf: 0.75, mean_coverage: 0.43, n: 590 },
      { band: '0.50 – 0.70', conf: 0.60, mean_coverage: 0.36, n: 1520 },
      { band: '0.30 – 0.50', conf: 0.40, mean_coverage: 0.24, n: 760 },
      { band: '0.00 – 0.30', conf: 0.15, mean_coverage: 0.15, n: 460 },
    ],
    // Distribution: how many of ALL generated suggestions fell in each band (generation-
    // time). Shown instead of calibration for auto-send-only operators.
    distribution: [
      { band: '0.90 – 1.00', conf: 0.95, n: 5210 },
      { band: '0.80 – 0.90', conf: 0.85, n: 2040 },
      { band: '0.70 – 0.80', conf: 0.75, n: 1480 },
      { band: '0.50 – 0.70', conf: 0.60, n: 2360 },
      { band: '0.30 – 0.50', conf: 0.40, n: 1180 },
      { band: '0.00 – 0.30', conf: 0.15, n: 690 },
    ],
  },
  agents: [
    { id: 1, name: 'Edoardo S', replies: 367, cov_median: 1.0, sym_mean: 0.95, prec_mean: 0.96, near_exact_pct: 0.64, defer_pct: 0.05, refund_pct: 0.04, segments: { near_exact: 250, light: 0, trimmed: 20, moderate: 35, heavy: 25, genuine_override: 37 } },
    { id: 2, name: 'Anderson B', replies: 400, cov_median: 1.0, sym_mean: 0.94, prec_mean: 0.95, near_exact_pct: 0.62, defer_pct: 0.05, refund_pct: 0.05, segments: { near_exact: 262, light: 0, trimmed: 25, moderate: 40, heavy: 25, genuine_override: 48 } },
    { id: 3, name: 'Michel Z', replies: 307, cov_median: 1.0, sym_mean: 0.93, prec_mean: 0.94, near_exact_pct: 0.58, defer_pct: 0.06, refund_pct: 0.05, segments: { near_exact: 190, light: 0, trimmed: 18, moderate: 30, heavy: 24, genuine_override: 45 } },
    { id: 4, name: 'Ana M', replies: 303, cov_median: 0.98, sym_mean: 0.9, prec_mean: 0.92, near_exact_pct: 0.54, defer_pct: 0.07, refund_pct: 0.06, segments: { near_exact: 175, light: 0, trimmed: 20, moderate: 33, heavy: 25, genuine_override: 50 } },
    { id: 5, name: 'Estefania A', replies: 266, cov_median: 0.9, sym_mean: 0.83, prec_mean: 0.88, near_exact_pct: 0.49, defer_pct: 0.1, refund_pct: 0.09, segments: { near_exact: 140, light: 0, trimmed: 15, moderate: 30, heavy: 25, genuine_override: 56 } },
    { id: 6, name: 'Nuria H', replies: 379, cov_median: 0.92, sym_mean: 0.85, prec_mean: 0.9, near_exact_pct: 0.46, defer_pct: 0.09, refund_pct: 0.08, segments: { near_exact: 190, light: 0, trimmed: 30, moderate: 45, heavy: 35, genuine_override: 79 } },
    // Karla: low Usage but HIGH precision -> a faithful trimmer (keeps the AI's words, cuts length)
    { id: 7, name: 'Karla R', replies: 365, cov_median: 0.61, sym_mean: 0.58, prec_mean: 0.86, near_exact_pct: 0.32, defer_pct: 0.12, refund_pct: 0.13, segments: { near_exact: 120, light: 0, trimmed: 110, moderate: 30, heavy: 30, genuine_override: 75 } },
    { id: 8, name: 'Enrica C', replies: 336, cov_median: 0.34, sym_mean: 0.41, prec_mean: 0.78, near_exact_pct: 0.18, defer_pct: 0.11, refund_pct: 0.12, segments: { near_exact: 70, light: 0, trimmed: 60, moderate: 25, heavy: 35, genuine_override: 146 } },
    // Lilian: low Usage AND low precision -> genuinely rewrites in her own voice
    { id: 9, name: 'Lilian G', replies: 439, cov_median: 0.18, sym_mean: 0.29, prec_mean: 0.33, near_exact_pct: 0.14, defer_pct: 0.21, refund_pct: 0.27, segments: { near_exact: 60, light: 0, trimmed: 20, moderate: 30, heavy: 40, genuine_override: 289 } },
    { id: 10, name: 'Federica B', replies: 323, cov_median: 0.11, sym_mean: 0.22, prec_mean: 0.28, near_exact_pct: 0.01, defer_pct: 0.37, refund_pct: 0.5, segments: { near_exact: 5, light: 0, trimmed: 10, moderate: 10, heavy: 20, genuine_override: 278 } },
  ],
  // SEPARATE feature: notifications-module auto-replies (Notification_Auto_Response),
  // split by whether the matched option used AI. sent_untyped = backfilled history from
  // before type-tracking shipped (total only).
  notifications: {
    sent_total: 3540,
    sent_ai: 1180,
    sent_manual: 1605,
    sent_untyped: 755,
    conversations_total: 2910,
    conversations_ai: 980,
    conversations_manual: 1410,
  },
};

// Sample per-channel split for the preview: a few agents also answer web chat after handoffs.
const SAMPLE_WEB_CHAT_SHARE = { 1: 0.12, 2: 0.08, 3: 0.1, 5: 0.05 };
MOCK_METRICS.agents = MOCK_METRICS.agents.map((a) => {
  const share = SAMPLE_WEB_CHAT_SHARE[a.id] || 0;
  const part = (n) => Math.round((n || 0) * share);
  const split = (segs, f) => Object.fromEntries(Object.entries(segs).map(([k, v]) => [k, f(v, part(v))]));
  const ww = { replies: part(a.replies), segments: split(a.segments, (_v, p) => p), cov_mean: a.cov_median, prec_mean: a.prec_mean, defer_pct: a.defer_pct };
  const support = { replies: a.replies - ww.replies, segments: split(a.segments, (v, p) => v - p), cov_mean: a.cov_median, prec_mean: a.prec_mean, defer_pct: a.defer_pct };
  return { ...a, by_channel: { web_widget: ww, support } };
});

// Exposed so tests can shape the sample data (e.g. a web chat with no activity).
export const MOCK_FOR_TESTS = MOCK_METRICS;

// Mock daily series for the temporal charts (deterministic gentle wave so the
// design is stable). Replaced by the endpoint's `daily` array when live.
function buildDailyMock(days = 30) {
  const out = [];
  const end = new Date('2026-06-12T00:00:00Z');
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(end.getUTCDate() - i);
    const label = `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
    const weekend = [0, 6].includes(d.getUTCDay());
    const suggestions = Math.round((weekend ? 280 : 460) + 80 * Math.sin(i / 3));
    const autoSendRate = +(13 + 5 * Math.sin(i / 4)).toFixed(1); // %
    const autoSent = Math.round((suggestions * autoSendRate) / 100);
    // Coverage-band mix (sums to 100). near_exact and rewritten move inversely.
    const near = +(33 + 6 * Math.sin(i / 5)).toFixed(1);
    const light = 3;
    const moderate = 6;
    const heavy = 9;
    const rewritten = +(100 - near - light - moderate - heavy).toFixed(1);
    // Channel split: the widget is a small, mostly auto-sent slice of the day.
    const wwSuggestions = Math.round(suggestions * 0.14);
    const wwAutoSent = Math.min(autoSent, Math.round(wwSuggestions * 0.87));
    out.push({
      date: label,
      suggestions,
      auto_sent: autoSent,
      shown: suggestions - autoSent,
      dropped: 0,
      ww_suggestions: wwSuggestions,
      ww_auto_sent: wwAutoSent,
      support_suggestions: suggestions - wwSuggestions,
      support_auto_sent: autoSent - wwAutoSent,
      near,
      light,
      moderate,
      heavy,
      rewritten,
      auto_send: autoSendRate,
      deferral: +(12 + 4 * Math.sin(i / 6)).toFixed(1), // %
      confidence: +(0.84 + 0.06 * Math.sin(i / 4)).toFixed(2), // 0-1
    });
  }
  return out;
}

// Per-agent daily series (deterministic from the agent's snapshot). Replaced by
// the rollup's per-agent daily rows when live (this is the v2 piece).
function buildAgentDailyMock(agent, days = 30) {
  const out = [];
  const end = new Date('2026-06-12T00:00:00Z');
  const baseCov = (agent.cov_median || 0) * 100;
  const perDay = (agent.replies || 0) / 30;
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end);
    d.setUTCDate(end.getUTCDate() - i);
    const label = `${String(d.getUTCMonth() + 1).padStart(2, '0')}/${String(d.getUTCDate()).padStart(2, '0')}`;
    const seed = agent.id * 7 + i;
    out.push({
      date: label,
      replies: Math.max(0, Math.round(perDay + 3 * Math.sin(seed / 3))),
      coverage: Math.max(0, Math.min(100, +(baseCov + 10 * Math.sin(seed / 5)).toFixed(1))),
    });
  }
  return out;
}

// Usage = a green ramp (dark = mostly used -> light = barely used), matching the hero
// zones. NOT a traffic light: low reuse isn't an error, just less of the draft kept.
// Bands at the same 65% / 40% thresholds as the spectrum; values are the hero numbers.
const usageHex = (v) => (v >= 0.65 ? '#2e7d32' : v >= 0.4 ? '#689f38' : '#8bc34a');
const sparkHex = usageHex; // recharts needs hex strokes (not MUI palette names)

const pct = (v) => `${Math.round((v || 0) * 100)}%`;
const pct1 = (v) => `${((v || 0) * 100).toFixed(1)}%`;
const num = (v) => (v || 0).toLocaleString();


// Confidence -> color ramp, copied from the chat screen's "AI Suggested Reply"
// confidence chip (ChatScreen.js) so the dashboard matches it: deep red at low
// confidence -> amber -> deep green at 90%+. Indexed in 5% steps.
const CONFIDENCE_GRADIENT = [
  '#B71C1C', '#C62828', '#D32F2F', '#D84315', '#E53935', '#E64A19', '#EF6C00', '#F57C00',
  '#FF8F00', '#FF9800', '#FFA000', '#FFB300', '#FFC107', '#FFCA28', '#FFD54F', '#FDD835',
  '#C0CA33', '#7CB342', '#43A047', '#2E7D32',
];
const confidenceFill = (conf) => CONFIDENCE_GRADIENT[Math.max(0, Math.min(Math.floor(Math.round((conf || 0) * 100) / 5), 19))];

// Build vertical-gradient stops so a trend line is colored by its *absolute*
// value via the confidence ramp (peaks greener, dips toward amber/red). recharts
// strokes a line gradient against the line's bounding box, so we map each height
// back to its real value: offset 0% = the line's max, 100% = its min.
function valueGradientStops(values, fill = confidenceFill, steps = 24) {
  if (!values.length) return [{ offset: 0, color: fill(0) }];
  const min = Math.min(...values);
  const max = Math.max(...values);
  // Flat series (e.g. a high-confidence operator whose median is 0.95 every day):
  // a solid color, so we don't build a ramp that runs past the data into an
  // out-of-range fill index (which produced an undefined stop color -> broken
  // gradient -> invisible line).
  if (max === min) return [{ offset: 0, color: fill(max) }];
  const span = max - min;
  const stops = [];
  for (let k = 0; k <= steps; k++) {
    stops.push({ offset: (k / steps) * 100, color: fill(max - (span * k) / steps) });
  }
  return stops;
}

// CP-8222's tap-to-open InfoTip now lives in components/InfoTip.js (shared with the
// Trip editor's Internal Name field, CP-8309).

function StatCard({ label, value, sub, hint }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: '12px', height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {label}
          </Typography>
          {hint && (
            <InfoTip title={hint} sx={{ fontSize: 14, color: 'text.disabled' }} />
          )}
        </Stack>
        <Typography variant="h4" sx={{ fontWeight: 600, mt: 0.5 }}>{value}</Typography>
        {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function PctBar({ label, value, color = 'primary', right, hexColor }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">{right != null ? right : pct(value)}</Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.round((value || 0) * 100))}
        color={hexColor ? undefined : color}
        sx={{
          height: 8,
          borderRadius: 4,
          // For custom-colored bars MUI would otherwise leave a blue (primary)
          // track behind the fill, which reads as a misleading second category.
          ...(hexColor && {
            backgroundColor: '#eceff1',
            '& .MuiLinearProgress-bar': { backgroundColor: hexColor },
          }),
        }}
      />
    </Box>
  );
}

// The hero: one 100%-stacked bar showing how much of every AI draft staff used,
// with the <40% region split into genuine failure vs. not-a-draft-issue.
function CoverageSpectrum({ segments }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', width: '100%', height: 32, borderRadius: '8px', overflow: 'hidden' }}>
        {segments.map((s) => (
          <Tooltip key={s.key} title={`${s.label} — ${pct1(s.pct)}`} arrow>
            <Box
              sx={{
                width: `${s.pct * 100}%`,
                bgcolor: s.color,
                borderRight: '1px solid rgba(255,255,255,0.6)',
                '&:last-of-type': { borderRight: 0 },
              }}
            />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.75, mt: 1.5 }}>
        {segments.map((s) => (
          <Stack key={s.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: s.color }} />
            <Typography variant="caption" color="text.secondary">
              {s.legend} <b>{pct(s.pct)}</b>
            </Typography>
          </Stack>
        ))}
      </Box>
    </Box>
  );
}

function HeroFigure({ value, label, sub, hint, color = 'text.primary', swatch }) {
  // The info icon rides INSIDE the label, glued to the last word by a nowrap span:
  // when a label wraps over a narrow zone ("Strong starting / point ⓘ") the icon
  // stays tight against the final word instead of floating vertically centered
  // beside the whole two-line block. Swatch top-aligns to the first line for the
  // same reason.
  const words = String(label).split(' ');
  const lastWord = words.pop();
  const headWords = words.join(' ');
  return (
    <Box>
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        {swatch && <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: swatch, flex: 'none', mt: '5px' }} />}
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {headWords ? `${headWords} ` : ''}
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {lastWord}
            {hint && (
              <InfoTip title={hint} sx={{ fontSize: 14, color: 'text.disabled', verticalAlign: 'text-bottom', ml: 0.5 }} />
            )}
          </Box>
        </Typography>
      </Stack>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

// Per-agent table: each agent's scored replies grouped by outcome, with the hero bar's own names
// and colours, and the minutes the AI's drafts saved them. Needs agents[].segments from the
// report (reuse-band counts); until the backend sends it the table keeps Draft kept / AI wording.
const seg = (g, k) => (g && g[k]) || 0;
const OUTCOME_GROUPS = [
  { key: 'ready', label: 'Ready to send', color: BAND.near_exact, count: (g) => seg(g, 'near_exact') + seg(g, 'light') },
  { key: 'strong', label: 'Strong starting point', color: BAND.trimmed, count: (g) => seg(g, 'trimmed') + seg(g, 'moderate') + seg(g, 'heavy') },
  { key: 'reworded', label: 'Staff reworded', color: BAND.override, count: (g) => seg(g, 'genuine_override') },
];
// Same credits as the Staff time saved panel (backend TIME_SAVED_CREDIT_MIN).
const agentMinutesSaved = (g) => (seg(g, 'near_exact') + seg(g, 'light') + seg(g, 'trimmed')) * 1.5
  + seg(g, 'moderate') * 1 + seg(g, 'heavy') * 0.5;

// An agent's AI deferral rate at or above this is shown in bold: the AI often couldn't answer
// their questions, usually a gap in its knowledge base for that queue.
const DEFER_FLAG = 0.2;

// Column heading whose WORD is centred over the column: the info icon hangs just past its right
// edge instead of shifting the word left.
function ColHead({ label, tip }) {
  return (
    <Box component="span" sx={{ position: 'relative', display: 'inline-block' }}>
      {label}
      <Box component="span" sx={{ position: 'absolute', left: '100%', top: '50%', transform: 'translateY(-50%)', display: 'inline-flex' }}>
        <InfoTip title={tip} sx={{ fontSize: 13, ml: 0.5, color: 'text.disabled' }} />
      </Box>
    </Box>
  );
}

// A right-aligned number in a fixed-width block, so the block can centre under its heading
// while the digits still line up down the column.
function NumBlock({ width, color, sx, children }) {
  return (
    <Typography variant="body2" component="span" color={color}
                sx={{ display: 'inline-block', width, textAlign: 'right', fontVariantNumeric: 'tabular-nums', ...sx }}>
      {children}
    </Typography>
  );
}

function OutcomeBar({ segments }) {
  const groups = OUTCOME_GROUPS.map((o) => ({ ...o, n: o.count(segments) }));
  const total = groups.reduce((t, o) => t + o.n, 0) || 1;
  return (
    // One compact block (bar + percentages), so the tooltip opens right above whichever the
    // cursor is on.
    <Tooltip
      arrow
      placement="top"
      title={<TipParagraphs paragraphs={groups.map((o) => `${o.label}: ${num(o.n)} replies (${pct(o.n / total)})`)} />}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ display: 'inline-flex', cursor: 'default' }}>
        <Box sx={{ display: 'flex', width: 140, height: 10, borderRadius: '5px', overflow: 'hidden', border: '1px solid #c5d6c7' }}>
          {groups.map((o) => (
            <Box key={o.key} sx={{ width: `${(100 * o.n) / total}%`, bgcolor: o.color }} />
          ))}
        </Box>
        <Typography variant="body2" sx={{ minWidth: 104, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          {groups.map((o) => pct(o.n / total)).join(' · ')}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

// A tooltip laid out as short paragraphs.
export function TipParagraphs({ paragraphs }) {
  const p = { display: 'block', fontSize: 'inherit', lineHeight: 1.45 };
  return (
    <Stack spacing={0.75} sx={{ py: 0.25 }}>
      {paragraphs.map((text) => (
        <Typography key={text} component="span" sx={p}>{text}</Typography>
      ))}
    </Stack>
  );
}

// The reuse scores' tooltips. Each explains its own score and uses the same two drafts (one the
// agent trimmed, one they added to) to show what moves THAT number, so the pair can be read
// without the old coverage/precision vocabulary.
export const REUSE_TIPS = {
  // Per-agent column: share of the draft that survived into the reply.
  draftKept: [
    'How much of the AI’s suggested reply this agent kept in what they sent, on average.',
    'Cutting the draft lowers it. If the AI drafts 100 words and the agent sends 40 of them, Draft kept is 40%.',
    'Adding to the draft does not. If the agent sends all 60 words of a draft plus 40 of their own, Draft kept is still 100%.',
    'A low score means this agent usually cuts most of the AI’s reply or replaces it.',
  ],
  // Per-agent column: share of the reply that came from the draft.
  aiWording: [
    'How much of what this agent sent was the AI’s own words, on average.',
    'Adding their own words lowers it. If the agent sends a 60-word draft plus 40 words of their own, AI wording is 60%.',
    'Cutting the draft does not. If the agent sends 40 words, all taken from a 100-word draft, AI wording is still 100%.',
    'A low score means most of what this agent sends is their own writing.',
  ],
};

const minutes = (v) => `${Number(v)} min`;
// Whole hours from 10 up (minutes are noise at that size), one decimal below.
const hoursText = (totalMin) => {
  const h = (totalMin || 0) / 60;
  return h >= 10 ? Math.round(h).toLocaleString() : h.toFixed(1);
};
// A full-time employee's hours: the "like having N extra full-time employees" line divides
// the time saved by this per week of the selected range, and is hidden below FTE_LINE_MIN.
const FTE_HOURS_PER_WEEK = 40;
const FTE_LINE_MIN = 0.5;
// The "Staff time saved" panel is hidden for now (Ben, 2026-11-01: the estimate reads too
// conservative). Its other two figures moved to the guest conversation cards for good ("needed
// no one from your team" and the AI's after-hours share), so the panel is the hours alone. Set
// true to bring it back.
export const SHOW_TIME_SAVED_HOURS = false;

// "last 30 days" for a preset range; the dates for a custom one ("Jan 1 – Sep 29, 2026").
export function periodLabel(preset, start, end) {
  if (preset && PRESET_LABEL[preset]) return PRESET_LABEL[preset].toLowerCase();
  const s = moment(start);
  const e = moment(end);
  if (!s.isValid() || !e.isValid()) return '';
  return s.year() === e.year()
    ? `${s.format('MMM D')} – ${e.format('MMM D, YYYY')}`
    : `${s.format('MMM D, YYYY')} – ${e.format('MMM D, YYYY')}`;
}

// Exact minutes from the counts x credits in the payload. The backend's hours_* fields are
// rounded to 0.1 h, which would make every minute figure a multiple of 6.
function timeSavedMinutes(ts) {
  const c = ts.credits_min || {};
  const d = ts.drafts_used || {};
  const auto = (ts.auto_replies || 0) * (c.auto_reply || 0);
  const drafts = (d.near || 0) * (c.draft_near || 0)
    + (d.moderate || 0) * (c.draft_moderate || 0)
    + (d.heavy || 0) * (c.draft_heavy || 0);
  const ww = ts.channels?.web_widget || {};
  const wwDrafts = ww.drafts_used || {};
  const webWidget = (ww.auto_replies || 0) * (c.auto_reply || 0)
    + (wwDrafts.near || 0) * (c.draft_near || 0)
    + (wwDrafts.moderate || 0) * (c.draft_moderate || 0)
    + (wwDrafts.heavy || 0) * (c.draft_heavy || 0);
  return { total: auto + drafts, auto, drafts, webWidget, support: auto + drafts - webWidget };
}

// CP-8400 "Staff time saved": a marketing-style ESTIMATE, so the figure is labelled as one and
// the InfoTip spells out the credits the backend used (time_saved.credits_min) and what earned
// nothing. Framed as capacity added to the team ("like having N extra full-time employees"),
// never as people replaced: agents read this page too.
// One channel's slice of time_saved (the web chat page): its AI replies and drafts only. The
// after-hours share isn't split by channel, so it's left out rather than shown for both.
function channelTimeSaved(ts, key) {
  const c = ts && ts.channels && ts.channels[key];
  if (!c) return ts;
  return { ...ts, auto_replies: c.auto_replies, drafts_used: c.drafts_used || {}, channels: null, after_hours_pct: null };
}

export function TimeSavedPanel({ ts, showChannels, period, windowDays }) {
  if (!SHOW_TIME_SAVED_HOURS) return null;
  const c = ts.credits_min || {};
  const ex = ts.excluded || {};
  const mins = timeSavedMinutes(ts);
  const fte = windowDays > 0 ? (mins.total / 60) / ((windowDays / 7) * FTE_HOURS_PER_WEEK) : 0;
  const hint = `An estimate, not a measurement: ${minutes(c.auto_reply)} for each reply the AI sent on its own, and for each AI draft your team sent — ${minutes(c.draft_near)} if they used it nearly as-is, ${minutes(c.draft_moderate)} with moderate edits, ${minutes(c.draft_heavy)} if they kept only part of it. Drafts your team rewrote count for nothing. Also left out: ${num(ex.holding_messages)} "passed to our team" holding messages, ${num(ex.repeats)} repeated replies and ${num(ex.deferrals)} "let me check" replies. The full-time line compares the hours with a ${FTE_HOURS_PER_WEEK}-hour week over the selected dates.`;
  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Staff time saved</Typography>
        {period && (
          <Typography variant="subtitle1" color="text.secondary">· {period}</Typography>
        )}
        <InfoTip title={hint} sx={{ fontSize: 15, color: 'text.disabled' }} />
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={1.5} flexWrap="wrap">
        <Typography variant="h3" sx={{ fontWeight: 700, color: BAND.auto_sent }}>
          {hoursText(mins.total)}
          <Box component="span" sx={{ fontSize: '0.45em', fontWeight: 500, ml: 1 }}>hours</Box>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
          Estimated
        </Typography>
      </Stack>
      {fte >= FTE_LINE_MIN && (
        <Typography variant="body1" sx={{ fontWeight: 500, mt: 0.25 }}>
          Like having {fte.toFixed(1)} extra full-time employees on your team
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {hoursText(mins.auto)} h from {num(ts.auto_replies)} replies the AI sent itself · {hoursText(mins.drafts)} h from AI drafts your team used
      </Typography>
      {showChannels && ts.channels?.web_widget && ts.channels?.support && (
        <Typography variant="body2" color="text.secondary">
          Web chat {hoursText(mins.webWidget)} h · Support chat {hoursText(mins.support)} h
        </Typography>
      )}
    </Paper>
  );
}

// Office hours as people say them: "07:15" -> "7:15am", "18:00" -> "6pm", "00:00" -> "midnight".
export function hourText(hhmm) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (Number.isNaN(h)) return '';
  if (h === 0 && !m) return 'midnight';
  if (h === 12 && !m) return 'noon';
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 || 12;
  return `${h12}${m ? `:${String(m).padStart(2, '0')}` : ''}${suffix}`;
}

const WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// ["Mon".."Sun"] -> "daily"; runs of 3+ days collapse: ["Mon".."Fri"] -> "Mon–Fri".
export function daysText(days) {
  const idx = WEEK.map((d, i) => (days || []).includes(d) ? i : -1).filter((i) => i >= 0);
  if (idx.length === 7) return 'daily';
  const parts = [];
  for (let i = 0; i < idx.length; i++) {
    let j = i;
    while (j + 1 < idx.length && idx[j + 1] === idx[j] + 1) j++;
    if (j - i >= 2) parts.push(`${WEEK[idx[i]]}–${WEEK[idx[j]]}`);
    else for (let k = i; k <= j; k++) parts.push(WEEK[idx[k]]);
    i = j;
  }
  return parts.join(', ');
}

// "Europe/Madrid" -> "Madrid time", "America/New_York" -> "New York time".
export function zoneText(tz) {
  if (!tz) return '';
  if (tz === 'UTC' || tz === 'Etc/UTC') return 'UTC';
  return `${tz.split('/').pop().replace(/_/g, ' ')} time`;
}

export function scheduleText(row) {
  return `${hourText(row.start)}–${hourText(row.end)} ${daysText(row.days)}`;
}

// Guest messages (report `inbound`): how many conversations guests started or continued in the
// dates, and how many of their messages arrived outside office hours -- every guest message,
// whatever the AI did with it. `basis` says which hours: the operator's Office Hours, or 9am-5pm
// in its timezone when none are set. `block` is the selected channel's slice (or the total).
// `officeHours` is the CURRENT schedule (report inbound.office_hours), printed on the card so a
// low or high share explains itself; messages were judged against the hours set at the time.
// `containment` (report containment, or its channel slice) adds a third card, "Needed no one from
// your team", once the AI fully handled at least one conversation: a 0% (or an operator without
// auto-replies, where there's nothing to count) would only undersell it. `aiAfterHoursPct` (the
// AI's own replies sent outside office hours; operator-wide, so only for all channels) goes under
// the guests' after-hours share. Both used to sit in the Staff time saved panel.
export function GuestInboundCards({ block, basis, channel, officeHours, timezone, containment, aiAfterHoursPct }) {
  if (!block) return null;
  const showContainment = !!(containment && containment.handled > 0);
  const item = showContainment ? { xs: 12, sm: 6, md: 4 } : { xs: 12, sm: 6 };
  const onOfficeHours = basis === 'office_hours';
  const where = channel === 'web_widget'
    ? 'in the website chat'
    : channel === 'support' ? 'in support chat' : 'in web chat and support chat';
  const known = block.after_hours_pct != null;
  const showAiAfterHours = onOfficeHours && known && aiAfterHoursPct != null;
  const schedule = onOfficeHours ? (officeHours || []) : [];
  const zone = zoneText((schedule[0] && schedule[0].timezone) || timezone);
  let hoursLine = null;
  if (schedule.length === 1) hoursLine = `Office hours ${scheduleText(schedule[0])}${zone ? `, ${zone}` : ''}`;
  else if (schedule.length > 1) hoursLine = 'Office hours vary by day';
  const officeTips = schedule.length
    ? [
      `Your office hours: ${schedule.map(scheduleText).join('; ')}${zone ? ` (${zone})` : ''}.`,
      'If you change your office hours, messages from before the change keep the hours they were checked against.',
    ]
    : [];
  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      <Grid item {...item}>
        <StatCard label="Conversations" value={num(block.conversations)}
                  sub={`${num(block.messages)} messages from guests`}
                  hint={<TipParagraphs paragraphs={[
                    `Conversations where a guest wrote ${where} in these dates — every one, whether the AI answered, your team did, or no one has yet.`,
                    'A conversation that runs over several days counts once. Group chats and announcements aren’t included.',
                  ]} />} />
      </Grid>
      <Grid item {...item}>
        <StatCard label={onOfficeHours ? 'Messages received outside office hours' : 'Messages received outside 9am–5pm'}
                  value={known ? pct(block.after_hours_pct) : '—'}
                  sub={known
                    ? (
                      <>
                        {`of ${num(block.messages)} messages from guests`}
                        {hoursLine && <><br />{hoursLine}</>}
                        {showAiAfterHours && (
                          <><br />{`${pct(aiAfterHoursPct)} of the AI’s own replies went out then`}</>
                        )}
                      </>
                    )
                    : 'Set your office hours (Settings → General) to see this'}
                  hint={<TipParagraphs paragraphs={
                    onOfficeHours
                      ? [
                        'Messages from guests that arrived while your office hours say your team is away. The AI can answer these straight away instead of leaving them for the next shift.',
                        ...(showAiAfterHours
                          ? ['The last line is the share of the replies the AI sent on its own that went out while your team was away.']
                          : []),
                        ...officeTips,
                      ]
                      : [
                        `You haven’t set office hours, so this counts messages that arrived before 9am or after 5pm, any day of the week, ${zone ? `${zone}` : 'in your local time'}.`,
                        'Set your office hours under Settings → General → Office Hours to use your real hours.',
                      ]
                  } />} />
      </Grid>
      {showContainment && (
        <Grid item {...item}>
          <StatCard label="Needed no one from your team" value={pct(containment.handled_pct)}
                    sub={`of ${num(containment.answered)} conversations the AI answered`}
                    hint={<TipParagraphs paragraphs={[
                      `Of the ${num(containment.answered)} conversations where the AI sent a real answer, ${num(containment.handled)} had no reply from your team and weren’t handed over to them.`,
                      'A reply from a staff member or guide counts; automated messages, reminders and notifications don’t.',
                      'This can’t tell whether a guest who stopped replying got their answer or gave up, so it says “needed no one”, not “resolved”.',
                    ]} />} />
        </Grid>
      )}
    </Grid>
  );
}

// What happened to each web chat conversation (report inbound.channels.web_widget.outcomes):
// one bucket per conversation, so the bar adds up to the Conversations card.
const WEB_CHAT_OUTCOMES = [
  { key: 'ai', legend: 'Answered by the AI', color: BAND.auto_sent,
    tip: 'The AI answered and nothing was handed over to your team.' },
  { key: 'team', legend: 'Answered by your team', color: '#1565c0',
    tip: 'Someone from your team replied, in the chat or by email after the visitor left their address.' },
  { key: 'email', legend: 'Left an email', color: '#64b5f6',
    tip: 'The visitor left their email after the AI handed over, and your team hasn’t replied yet.' },
  { key: 'unanswered', legend: 'Handed off, no staff reply', color: '#ffb74d',
    tip: 'The AI passed the visitor to your team (or said it would check with them), nobody replied in the chat, and the visitor didn’t leave an email.' },
];

export function WebChatOutcomes({ outcomes }) {
  if (!outcomes) return null;
  const total = WEB_CHAT_OUTCOMES.reduce((sum, o) => sum + (outcomes[o.key] || 0), 0);
  if (!total) return null;
  const rows = WEB_CHAT_OUTCOMES.map((o) => ({ ...o, n: outcomes[o.key] || 0, share: (outcomes[o.key] || 0) / total }));
  return (
    <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>What happened to web chat conversations</Typography>
        <InfoTip
          title={<TipParagraphs paragraphs={[
            `Each of the ${num(total)} conversations counted once, by how it ended up.`,
            ...rows.map((o) => `${o.legend}: ${o.tip}`),
            'A reply your team sends after these dates still counts.',
          ]} />}
          sx={{ fontSize: 15, color: 'text.disabled' }}
        />
      </Stack>
      <Box sx={{ display: 'flex', width: '100%', height: 24, borderRadius: '8px', overflow: 'hidden' }}>
        {rows.filter((o) => o.n > 0).map((o) => (
          <Tooltip key={o.key} title={`${o.legend}: ${num(o.n)} (${pct(o.share)})`} arrow>
            <Box sx={{ width: `${o.share * 100}%`, bgcolor: o.color, borderRight: '1px solid rgba(255,255,255,0.6)', '&:last-of-type': { borderRight: 0 } }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2.5, rowGap: 0.75, mt: 1.5 }}>
        {rows.map((o) => (
          <Stack key={o.key} direction="row" spacing={0.75} alignItems="center">
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: o.color }} />
            <Typography variant="body2" color="text.secondary">
              {o.legend} <b>{num(o.n)}</b> ({pct(o.share)})
            </Typography>
          </Stack>
        ))}
      </Box>
    </Paper>
  );
}

// Channel segment (web chat widget vs support chat). Only offered when the operator has
// the widget (modes.web_chat_widget_available) AND the report carries `channels`.
// 'all' = the operator-wide `usage` figures, exactly as before the split.
const CHANNELS = [
  { key: 'all', label: 'All channels' },
  { key: 'support', label: 'Support chat' },
  { key: 'web_widget', label: 'Web chat' },
];
const CHANNEL_LABEL = { support: 'Support chat', web_widget: 'Web chat' };
// Daily-row keys per channel (the rollup writes ww_* twins; support = total - ww).
const CHANNEL_DAILY_PREFIX = { support: 'support', web_widget: 'ww' };

// How each AI reply was produced -- mirrors computed_ai_auto_reply["source"]. Instant
// answers and trigger replies are configured text (no generation); AI-generated is RAG.
// All three are billable; the split is what operators asked for.
const REPLY_KINDS = [
  { key: 'rule_exact', legend: 'AI Quick Response', label: 'The chatbot recognised one of your common questions and gave its saved answer instantly', color: '#1565c0' },
  { key: 'rule', legend: 'AI Reply Rule', label: 'The chatbot recognised what the guest was asking and used the reply you configured for it', color: '#64b5f6' },
  { key: 'rag', legend: 'AI Insight Engine', label: 'The chatbot wrote an answer from your knowledge base', color: '#66bb6a' },
  { key: 'missed_tour', legend: 'Rebooking offer', label: 'The chatbot offered a guest who missed their tour another time, for your rebooking fee', color: '#ffb74d' },
  { key: 'unknown', legend: 'Kind not recorded', label: 'Suggestions from before the reply kind was tracked (2026-09-02)', color: '#cfd8dc' },
];

// One 100%-stacked bar per channel showing the reply-kind mix. Zero-count kinds are
// dropped from the bar and legend (the "unknown" bucket only appears on pre-split days).
function ReplyKindBar({ title, block }) {
  const gen = block?.suggestions_generated || 0;
  const src = block?.by_source || {};
  const segments = REPLY_KINDS
    .map((k) => ({ ...k, n: src[k.key] || 0, pct: gen ? (src[k.key] || 0) / gen : 0 }))
    .filter((s) => s.n > 0);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">{num(gen)} replies</Typography>
      </Stack>
      {gen > 0 ? (
        <CoverageSpectrum segments={segments.map((s) => ({ ...s, label: `${s.legend}: ${s.label}` }))} />
      ) : (
        <Typography variant="caption" color="text.secondary">No AI replies in this window.</Typography>
      )}
    </Box>
  );
}

// Map the report/ai_metrics payload (stable keys + numbers) to the component's
// display shape, reusing MOCK_METRICS' spectrum/override templates for the colours
// and labels (presentation stays the frontend's concern).
// The reply usage bar's figures from a channel's reuse block (report channels.<ch>.reuse):
// the same zones fromApi builds for "All channels" (CP-8400: staff reply in web chat too).
function adoptionFromReuse(reuse) {
  const pairs = reuse.pairs_scored || 1;
  const seg = reuse.segments || {};
  const n = (k) => seg[k] || 0;
  return {
    pairs_scored: reuse.pairs_scored || 0,
    cov_mean: reuse.cov_mean,
    sym_mean: reuse.sym_mean,
    prec_mean: reuse.prec_mean,
    mostly_used_pct: (n('near_exact') + n('light')) / pairs,
    partially_used_pct: (n('moderate') + n('heavy') + n('trimmed')) / pairs,
    genuine_override_pct: n('genuine_override') / pairs,
    not_applicable_pct: (n('ai_deferred') + n('ops_template')) / pairs,
    spectrum: MOCK_METRICS.adoption.spectrum.map((sp) => ({ ...sp, pct: n(sp.key) / pairs })),
  };
}

export function fromApi(api) {
  const pairs = api.adoption.pairs_scored || 1;
  const seg = api.adoption.segments || {};
  const z = api.adoption.zones || {};
  const orMap = api.adoption.override_reasons || {};
  return {
    operator: api.operator,
    window: api.window,
    modes: api.modes,
    usage: api.usage,
    adoption: {
      pairs_scored: api.adoption.pairs_scored,
      cov_median: api.adoption.cov_median,
      cov_mean: api.adoption.cov_mean,
      sym_mean: api.adoption.sym_mean,
      prec_mean: api.adoption.prec_mean,
      // Re-aggregate the two top zones from the raw segment counts so a *moderate*
      // edit (65-85% kept = real editing) lands in "Strong starting point", not
      // "Ready to send" (>=85%). Done here (not the backend z.mostly/z.partially) to
      // avoid a backend redeploy/re-backfill. `trimmed` (faithful low-coverage subset)
      // joins "Strong starting point" -- it's reuse, not a rewrite.
      mostly_used_pct: ((seg.near_exact || 0) + (seg.light || 0)) / pairs,
      partially_used_pct: ((seg.moderate || 0) + (seg.heavy || 0) + (seg.trimmed || 0)) / pairs,
      genuine_override_pct: z.genuine_override,
      not_applicable_pct: z.not_applicable,
      spectrum: MOCK_METRICS.adoption.spectrum.map((s) => ({ ...s, pct: (seg[s.key] || 0) / pairs })),
      override_reasons: MOCK_METRICS.adoption.override_reasons.map((r) => ({ ...r, pct: orMap[r.key] ?? 0 })),
    },
    confidence: {
      median: api.confidence.median,
      calibration: api.confidence.calibration || [],
      distribution: api.confidence.distribution || [],
    },
    // cov_mean aliased to cov_median for the table (per-agent is a mean -- no histogram stored).
    agents: (api.agents || []).map((a) => ({ ...a, cov_median: a.cov_mean })),
    daily: api.daily || [],
    // Web chat vs support split (null on a backend that predates it -> control hidden).
    channels: api.channels || null,
    notifications: api.notifications || {},
    // Null on a backend that predates CP-8400 -> the panel is hidden.
    time_saved: api.time_saved || null,
    // "Conversations handled entirely by AI"; null when hidden by the flag or nothing answered.
    containment: api.containment || null,
    // Guest messages: conversations, share outside office hours, web chat outcomes. Null on a
    // backend that predates it, or when no guest wrote in the dates -> the cards are hidden.
    inbound: api.inbound || null,
  };
}

// Date-range floor (matches the backend clamp: rollup data doesn't go back before 2026)
// and the preset -> trailing-days map. Day arithmetic mirrors the other metrics pages
// (Agent/Notification): "last N" anchors N days before today in the selected timezone.
const RANGE_FLOOR = '2026-01-01';
const PRESET_DAYS = { 'last 7': 7, 'last 30': 30, 'last 90': 90, 'last 365': 365 };
const DEFAULT_PRESET = 'last 30';
const STORAGE_KEY_AI_METRICS_TZ = 'aiMetrics_timezone';

function getLocalIanaTimezone() {
  let intlTz = null;
  try { intlTz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { /* ignore */ }
  let guessed = null;
  try { guessed = moment.tz.guess(); } catch { /* ignore */ }
  const candidate = intlTz && moment.tz.zone(intlTz) ? intlTz
    : (guessed && moment.tz.zone(guessed) ? guessed : null);
  return candidate || 'UTC';
}

function readStoredMetricsTimezoneOrLocalDefault(storageKey) {
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (stored && moment.tz.zone(stored)) return stored;
  } catch { /* quota / privacy mode */ }
  return getLocalIanaTimezone();
}

// {from, to} calendar dates (YYYY-MM-DD) for a preset, computed in `tz`.
function presetRange(key, tz) {
  const z = tz || 'UTC';
  const to = moment.tz(z).format('YYYY-MM-DD');
  if (key === 'all time') return { from: RANGE_FLOOR, to };
  const n = PRESET_DAYS[key] || PRESET_DAYS[DEFAULT_PRESET];
  return { from: moment.tz(z).subtract(n, 'days').format('YYYY-MM-DD'), to };
}

const PRESET_LABEL = { 'last 7': 'Last 7 days', 'last 30': 'Last 30 days', 'last 90': 'Last 90 days', 'last 365': 'Last 365 days', 'all time': 'All time' };
// Pill presets shown in the toolbar (compact labels), matching the Agent/Notification
// metrics pages' rounded pill style.
const RANGE_PRESETS = [{ key: 'last 7', label: 'Last 7' }, { key: 'last 30', label: 'Last 30' }, { key: 'last 90', label: 'Last 90' }];

// Custom action bar for the DateRangePicker: embeds the TimezonePicker inside the calendar
// popover (above Clear/OK), matching the Notification/Agent metrics pages. The picker passes
// the action-bar layout className/sx through `pickerActionBarProps`; we wrap it so the slot
// lands full-width below the calendars.
function AIMetricsRangePickerActionBar({ metricsTimezone, onMetricsTimezoneChange, ...pickerActionBarProps }) {
  const z = metricsTimezone || 'UTC';
  const { className, sx: layoutSx, ...pickersActionBarOnlyProps } = pickerActionBarProps;
  return (
    <Box
      className={className}
      sx={[
        { width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch', borderTop: 1, borderColor: 'divider' },
        ...(Array.isArray(layoutSx) ? layoutSx : [layoutSx].filter(Boolean)),
      ]}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Tooltip arrow placement="top-start" title={`Calendar day range uses: ${z}. Daily buckets are stored in UTC.`}>
          <Box sx={{ width: '100%', minWidth: { xs: 0, sm: 260 }, maxWidth: '100%' }}>
            <TimezonePicker
              label="Timezone"
              variant="outlined"
              size="small"
              fullWidth
              includeUtc
              showTimezoneOffset
              value={z}
              onChange={(_ev, option) => onMetricsTimezoneChange(option?.value || 'UTC')}
              componentsProps={{ paper: { sx: { maxWidth: 'min(560px, 94vw)' } } }}
            />
          </Box>
        </Tooltip>
      </Box>
      <PickersActionBar {...pickersActionBarOnlyProps} />
    </Box>
  );
}

// ``channelLock`` pins the page to one channel (the Web Chat Widget page's Metrics tab passes
// 'web_widget'): the Channel selector is hidden and every channel-aware section shows it.
// ``widgetId`` (CP-8335) narrows the report to one of the operator's branded web widgets.
export default function AIMetricsTab({ channelLock, widgetId } = {}) {
  const dataAPI = React.useContext(DataAPIContext);
  // Timezone-aware date range, matching the Agent/Notification metrics pages: the user's
  // tz (persisted in localStorage) drives the preset anchors and "today" ceiling; the
  // range is always sent as explicit from/to. `range.preset` records the active preset
  // (null once the user hand-picks a custom range) so the toggle + label stay correct.
  const [metricsTimezone, setMetricsTimezone] = useState(() => readStoredMetricsTimezoneOrLocalDefault(STORAGE_KEY_AI_METRICS_TZ));
  const z = metricsTimezone || 'UTC';
  const [range, setRange] = useState(() => ({ ...presetRange(DEFAULT_PRESET, metricsTimezone), preset: DEFAULT_PRESET }));
  const [pickerOpen, setPickerOpen] = useState(false);  // calendar-icon opens the range popover
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(DATA_SOURCE === 'live');
  const [error, setError] = useState(null);

  const handlePreset = (key) => { if (key) setRange({ ...presetRange(key, z), preset: key }); };
  // Stable so the memoized action-bar slot doesn't remount the TimezonePicker each render.
  const handleTzChange = React.useCallback((tz) => {
    if (!tz || !moment.tz.zone(tz)) return;
    try { window.localStorage.setItem(STORAGE_KEY_AI_METRICS_TZ, tz); } catch { /* ignore */ }
    setMetricsTimezone(tz);
    // Re-anchor an active preset to the new tz; a hand-picked custom range is left as-is.
    setRange((r) => (r.preset ? { ...presetRange(r.preset, tz), preset: r.preset } : r));
  }, []);
  const handleDateRangeChange = (val) => {
    const [s, e] = val || [];
    const fmt = (d) => (d && d.isValid && d.isValid() ? d.format('YYYY-MM-DD') : '');
    setRange({ from: fmt(s), to: fmt(e), preset: null });
  };
  const toMoment = (d) => (d ? moment.tz(d, 'YYYY-MM-DD', z) : null);
  const RangePickerActionBarSlot = React.useMemo(
    () => function RangePickerActionBarSlot(slotProps) {
      return (
        <AIMetricsRangePickerActionBar
          {...slotProps}
          metricsTimezone={metricsTimezone}
          onMetricsTimezoneChange={handleTzChange}
        />
      );
    },
    [metricsTimezone, handleTzChange],
  );

  React.useEffect(() => {
    if (DATA_SOURCE !== 'live' || !dataAPI) return undefined;
    if (!range.from || !range.to) return undefined;
    const ac = new AbortController();
    let active = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const filter = { date_from: range.from, date_to: range.to, timezone: z, ...(widgetId ? { widget: widgetId } : {}) };
        const res = await dataAPI(GET_ONE, 'report/ai_metrics', { filter, signal: ac.signal });
        if (!active) return;
        if (res?.statusCode >= 400 || res?.data?.error) {
          setError(`HTTP ${res?.statusCode || ''} ${res?.data?.error || ''}`.trim());
        } else {
          setLive(fromApi(res.data));
        }
      } catch (e) {
        if (active && e?.name !== 'AbortError') setError(String(e?.message || e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; ac.abort(); };
  }, [dataAPI, range.from, range.to, z, widgetId]);

  const liveMode = DATA_SOURCE === 'live' && !!live;
  // In production nothing below the date controls renders until real data arrives: the sample
  // data (MOCK_METRICS) is for the design preview only, never for an operator's first paint or
  // a failed request.
  const showData = DATA_SOURCE === 'mock' || liveMode;
  const showTrend = true; // per-agent daily now comes from the rollup (mock in preview)
  const m = liveMode ? live : MOCK_METRICS;
  // Reuse metrics (spectrum, replies-scored, per-agent usage, calibration) only mean
  // something when staff actually SEE the AI's drafts. With suggested replies off
  // (auto-send only) we don't score reuse, so those panels are hidden and replaced
  // with a note; auto-send volume/confidence stay.
  const reuseTracked = !!(m.modes && m.modes.auto_populate_suggested_response);
  // Channel split (web chat widget vs support chat), offered only to operators with the
  // widget. The volume cards, the per-day chart, the reply usage bar, Replies scored and the
  // per-agent table follow the selection -- staff do reply in web chat after a handoff
  // (CP-8400). Notification auto-replies stay operator-wide.
  const [channel, setChannel] = useState(channelLock || 'all');
  // The chips only appear when web chat has something to show in these dates (AI replies there,
  // or staff replies scored after a handoff) -- not merely because the widget is available.
  const webChat = m.channels && m.channels.web_widget;
  const webChatActive = !!(webChat && (webChat.suggestions_generated > 0 || (webChat.reuse && webChat.reuse.pairs_scored > 0)));
  const channelsOn = !!(m.modes && m.modes.web_chat_widget_available && webChatActive);
  // A locked page (the widget's Metrics tab) keeps its channel even when it's empty.
  const ch = channelLock && m.channels && m.channels[channelLock]
    ? channelLock
    : (channelsOn && m.channels[channel] ? channel : 'all');
  const usage = ch === 'all' ? m.usage : m.channels[ch];
  // The selected channel's reuse, when the report carries it (older backends: operator-wide).
  const channelReuse = ch !== 'all' && m.channels[ch] && m.channels[ch].reuse;
  const adoption = channelReuse ? adoptionFromReuse(channelReuse) : m.adoption;
  // The hero bar folds auto-sent replies in as its FIRST segment: operators read the
  // bar as "all AI replies", so a 100% covering only scored manual replies — with
  // auto-sends in a separate card — looked like two disagreeing totals. The reuse
  // segments rescale to (auto-sent + scored) so the combined bar still sums to 100%.
  // Follows the Channel selector. auto_sent_replies leaves out widget holding messages
  // ("passed to our team"), which aren't AI replies (CP-8400); older backends send auto_sent.
  const heroAutoSent = (usage && (usage.auto_sent_replies ?? usage.auto_sent)) || 0;
  const heroPairs = (adoption && adoption.pairs_scored) || 0;
  const heroTotal = heroAutoSent + heroPairs;
  const heroScale = heroTotal > 0 ? heroPairs / heroTotal : 1;
  const heroAutoPct = heroTotal > 0 ? heroAutoSent / heroTotal : 0;
  // With no auto-sends in the window the bar is exactly the old one (no empty
  // zero-width segment, no fifth figure, no layout shift).
  const heroSpectrum = heroAutoSent > 0
    ? [
      {
        key: 'auto_sent',
        group: 'auto',
        legend: 'Auto-sent by AI',
        label: 'Sent automatically by the AI at/above the confidence threshold — went out as-is, no staff edit',
        pct: heroAutoPct,
        color: BAND.auto_sent,
      },
      ...adoption.spectrum.map((s) => ({ ...s, pct: s.pct * heroScale })),
    ]
    : (adoption && adoption.spectrum) || [];
  // Headline figures as data so the row can render twice: positionally on md+ (each
  // figure over its bar zone), as a plain 2-per-row grid on xs. Zone order matches
  // the bar's segment order (each zone is a run of consecutive segments).
  const heroZones = [
    ...(heroAutoSent > 0 ? [{
      label: 'Auto-sent by AI', pct: heroAutoPct, color: '#1b5e20', swatch: BAND.auto_sent,
      hint: 'Replies the AI sent itself at/above the confidence threshold — went out as-is, no staff review. The remaining zones cover suggestions staff handled manually.',
    }] : []),
    {
      label: 'Ready to send', pct: (adoption.mostly_used_pct || 0) * heroScale, color: 'success.main', swatch: BAND.near_exact,
      hint: "Staff kept ≥85% of the AI's draft — sent as-is or with light touch-ups.",
    },
    {
      label: 'Strong starting point', pct: (adoption.partially_used_pct || 0) * heroScale, color: '#689f38', swatch: BAND.heavy,
      hint: "Staff kept 40–85% of the AI's draft (a moderate-to-heavier edit) — a solid base they built on.",
    },
    {
      label: 'Staff reworded', pct: (adoption.genuine_override_pct || 0) * heroScale, color: '#8bc34a', swatch: BAND.override,
      hint: "Staff kept under 40% of the AI's draft — they reworded it into their own reply. This is NOT proof the AI was wrong: staff may have reworded or added standard policy on top of a correct answer. Excludes AI deferrals and canned ops messages.",
    },
    {
      label: 'Deferred to staff', pct: (adoption.not_applicable_pct || 0) * heroScale, color: 'text.secondary', swatch: BAND.ai_deferred,
      hint: "The AI punted ('let me check with my team') or staff sent a canned operational message. Little of the suggestion was used, but not a usability issue — = AI deferred + ops template.",
    },
  ];
  const agents = [...m.agents].sort((a, b) => b.replies - a.replies);
  // New outcome + time-saved columns once the report carries per-agent segments.
  const hasOutcomes = agents.some((a) => a.segments);
  // Per-agent rows for the selected channel (report agents[].by_channel); an older report
  // without the split keeps every agent's operator-wide row.
  const agentsView = ch === 'all' || !agents.some((a) => a.by_channel)
    ? agents
    : agents
      .filter((a) => a.by_channel && a.by_channel[ch] && a.by_channel[ch].replies > 0)
      .map((a) => {
        const b = a.by_channel[ch];
        return { ...a, replies: b.replies, segments: b.segments, cov_median: b.cov_mean, prec_mean: b.prec_mean, defer_pct: b.defer_pct };
      })
      .sort((a, b) => b.replies - a.replies);
  // Effective span (inclusive) of the selected from/to range.
  const effectiveDays = Math.max(1, Math.round((new Date(range.to) - new Date(range.from)) / 86400000) + 1);
  const daily = liveMode ? (m.daily || []) : buildDailyMock(effectiveDays);
  // Per-channel daily rows only carry generated + auto-sent (no shown/dropped split), so
  // the channel view stacks "auto-sent" over "not auto-sent".
  const dailyView = ch === 'all'
    ? daily
    : daily.map((d) => {
      const p = CHANNEL_DAILY_PREFIX[ch];
      const s = d[`${p}_suggestions`] || 0;
      const a = d[`${p}_auto_sent`] || 0;
      return { ...d, suggestions: s, auto_sent: a, not_auto: Math.max(s - a, 0) };
    });
  // Per-agent daily trend: real rollup rows when live, deterministic mock in preview.
  const agentDaily = (a) => (liveMode ? (a.daily || []) : buildAgentDailyMock(a, effectiveDays));
  // Drop null confidence (empty/partial UTC days) so the gradient spans real values
  // only; the line itself gaps at those days (no connectNulls).
  const confVals = daily.map((d) => d.confidence).filter((v) => v != null);
  const confStops = valueGradientStops(confVals);
  // A perfectly flat line (e.g. a high-confidence operator at 0.95 every day) has a
  // ZERO-HEIGHT bounding box, and a vertical objectBoundingBox gradient on that
  // degenerates -> the stroke paints nothing. Fall back to a solid color when flat.
  const confFlat = confVals.length > 0 && Math.min(...confVals) === Math.max(...confVals);
  const confStroke = confFlat ? confidenceFill(confVals[0]) : 'url(#confLineGrad)';

  const [expanded, setExpanded] = useState(() => new Set());
  const toggleAgent = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  return (
    <Box>
      {loading && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>Loading metrics…</Alert>
      )}
      {error && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '12px' }}>
          Couldn’t load metrics ({error}). Reload the page or try a different date range.
        </Alert>
      )}
      {DATA_SOURCE === 'mock' && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
          Preview with sample data — design review only.
        </Alert>
      )}
      {DATA_SOURCE === 'live' && !liveMode && !loading && !error && (
        <Alert severity="info" sx={{ mb: 2, borderRadius: '12px' }}>
          No metrics for this date range yet.
        </Alert>
      )}

      {/* Header: title + date-range subtext (left), range controls (right) on one row. */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{channelLock === 'web_widget' ? 'Web chat metrics' : 'AI auto-reply metrics'}</Typography>
          <Typography variant="body2" color="text.secondary">
            {showData
              ? `${moment(m.window.start).format('MMM D, YYYY')} – ${moment(m.window.end).format('MMM D, YYYY')}`
              : `${moment(range.from).format('MMM D, YYYY')} – ${moment(range.to).format('MMM D, YYYY')}`}
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{
            // Hide the field's " – " separator, and tighten the From/To gap (its built-in
            // spacing:2 = 16px) so the two read as one grouped control.
            '& .MuiMultiInputDateRangeField-separator': { display: 'none' },
            '& .MuiMultiInputDateRangeField-root .MuiTextField-root ~ .MuiTextField-root': { marginLeft: '8px' },
            // CP-8220: on phones the two fixed-width From/To fields (150px each) ran past
            // the settings card's border. Below sm the field pair takes its own full row
            // and the two inputs split it evenly (the fields themselves drop their fixed
            // width via the textField slotProps below).
            '& .MuiMultiInputDateRangeField-root': { width: { xs: '100%', sm: 'auto' }, maxWidth: '100%' },
          }}
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {RANGE_PRESETS.map(({ key, label }) => {
              const selected = range.preset === key;
              return (
                <Box
                  key={key}
                  onClick={() => handlePreset(key)}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minWidth: 78, height: 28, px: 1.5, borderRadius: '20px',
                    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                    bgcolor: selected ? 'success.main' : '#D8D8D8',
                    color: selected ? 'common.white' : 'common.black',
                    '&:hover': { bgcolor: selected ? 'success.dark' : '#cccccc' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                </Box>
              );
            })}
          </Stack>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateRangePicker
              slots={{ field: MultiInputDateRangeField, actionBar: RangePickerActionBarSlot }}
              value={[toMoment(range.from), toMoment(range.to)]}
              onChange={handleDateRangeChange}
              format="MM/DD/YYYY"
              timezone={z}
              open={pickerOpen}
              onOpen={() => setPickerOpen(true)}
              onClose={() => setPickerOpen(false)}
              minDate={moment.tz(RANGE_FLOOR, 'YYYY-MM-DD', z)}
              maxDate={moment.tz(z)}
              slotProps={{
                // Two typeable From/To inputs styled like native date fields, each with a
                // calendar button that opens the range popover (where the timezone lives).
                textField: ({ position }) => ({
                  size: 'small',
                  label: position === 'start' ? 'From' : 'To',
                  // CP-8220: fluid on phones (two per row incl. the 8px gap), fixed 150px
                  // from sm up. minWidth 0 lets the inputs actually shrink inside the flex row.
                  sx: { width: { xs: 'calc(50% - 4px)', sm: 150 }, minWidth: 0 },
                  InputProps: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" edge="end" aria-label="Open calendar" onClick={() => setPickerOpen(true)}>
                          <CalendarTodayIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }),
                actionBar: { actions: ['clear', 'accept'] },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Stack>

      {showData && (
      <>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Chip size="small" color={m.modes.auto_populate_suggested_response ? 'success' : 'default'}
              variant={m.modes.auto_populate_suggested_response ? 'filled' : 'outlined'}
              label={`Suggested replies: ${m.modes.auto_populate_suggested_response ? 'On' : 'Off'}`} />
        {/* The Auto-send switch is support chat's: web chat always auto-sends while the widget is
            live, so its view leaves that chip out and shows the widget's own threshold (older
            backends without it: the shared one). */}
        {ch !== 'web_widget' && (
          <Chip size="small" color={m.modes.auto_reply_with_ai ? 'success' : 'default'}
                variant={m.modes.auto_reply_with_ai ? 'filled' : 'outlined'}
                label={`Auto-send: ${m.modes.auto_reply_with_ai ? 'On' : 'Off'}`} />
        )}
        <Chip size="small" variant="outlined" label={`Auto-send threshold: ${
          ch === 'web_widget' && m.modes.ai_auto_reply_min_confidence_web_widget != null
            ? m.modes.ai_auto_reply_min_confidence_web_widget
            : m.modes.ai_auto_reply_min_confidence
        }`} />
      </Stack>

      {/* Channel segment: web chat widget vs support chat (widget operators only). A page-level
          filter, so it sits with the mode chips above everything it drives. */}
      {channelsOn && !channelLock && (
        <>
        <Typography id="ai-metrics-channel-label" variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          Metrics for:
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}
               role="group" aria-labelledby="ai-metrics-channel-label">
          {CHANNELS.map(({ key, label }) => {
            const selected = ch === key;
            return (
              <Box
                key={key}
                onClick={() => setChannel(key)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  minWidth: 78, height: 28, px: 1.5, borderRadius: '20px',
                  cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
                  bgcolor: selected ? 'success.main' : '#D8D8D8',
                  color: selected ? 'common.white' : 'common.black',
                  '&:hover': { bgcolor: selected ? 'success.dark' : '#cccccc' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
              </Box>
            );
          })}
          <InfoTip
            title="Web chat = the website chat widget (anonymous visitors, no booking), including the replies your team sends there after the AI hands a conversation over. Support chat = guests and staff in the apps and dashboard. Everything on this page follows this selection except the notification auto-replies at the bottom."
            sx={{ fontSize: 15, color: 'text.disabled' }}
          />
        </Stack>
        </>
      )}


      {/* Live data only in production: while the report loads (or if it fails) `m` is the
          sample data, and the panel must never show to an operator the report would hide it
          from (ai_time_saved_enabled). The mock preview keeps it for design review. */}
      {(liveMode || DATA_SOURCE === 'mock') && m.time_saved && (m.time_saved.hours_total || 0) > 0 && (
        <TimeSavedPanel
          ts={ch !== 'all' ? channelTimeSaved(m.time_saved, ch) : m.time_saved}
          showChannels={channelsOn && ch === 'all'}
          // Live: the report's own window (the backend clamps the start to 2026-01-01).
          period={liveMode
            ? periodLabel(range.preset, m.window.start, m.window.end)
            : periodLabel(range.preset, range.from, range.to)}
          windowDays={liveMode ? m.window.days : effectiveDays}
        />
      )}

      {/* HERO: AI suggested reply usage spectrum, first on the page (Jordan, 2026-11-01) */}
      <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
          {/* With auto-sends folded into the bar the panel covers more than suggestions,
              so the title widens; without them it keeps the original name. */}
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{heroAutoSent > 0 ? 'AI reply usage' : 'AI suggested reply usage'}</Typography>
          <InfoTip title="Every AI reply in one bar: auto-sent replies first, then suggestions staff handled manually, scored by how much of the AI's draft they actually used in the message they sent. Greeting/signature are excluded; adding a booking block doesn't lower it. All segments are % of the combined total, so the bar sums to 100%." sx={{ fontSize: 15, color: 'text.disabled' }} />
        </Stack>
        {reuseTracked ? (
        <>
        <Typography variant="caption" color="text.secondary">
          {heroAutoSent > 0
            ? `Across ${num(heroTotal)} AI replies — ${num(heroAutoSent)} auto-sent + ${num(heroPairs)} manual replies that followed a suggestion`
            : `Across ${num(m.adoption.pairs_scored)} manual replies that followed a suggestion`}
        </Typography>

        {/* These figures are the bar's color-zone subtotals (auto-sent included when
            present); together they sum to 100% of all AI replies. On md+ each figure
            sits over ITS zone: flex-grow mirrors the zone's share (basis 0, so widths
            come out proportional and each label starts at its zone's left edge). The
            88px minWidth floors thin zones so labels wrap instead of overlapping —
            wide neighbours absorb the difference, costing a little positional accuracy
            only when a zone is tiny. The row is bottom-anchored so every percentage
            shares one baseline: a wrapped label (e.g. a 6% "Strong starting point"
            zone) grows UPWARD instead of pushing its number below its neighbours',
            and HeroFigure glues the info icon to the label's last word so it wraps
            with the text. Chosen over the measured stagger/scoot rows (72bfa6df,
            8e8216fd) — simpler and it reads fine. Phones keep the 2-per-row grid. */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'flex-end', my: 0.5 }}>
          {heroZones.map((z) => (
            <Box key={z.label} sx={{ flex: `${Math.max(z.pct || 0, 0.01)} 1 0px`, minWidth: 88, pr: 2 }}>
              <HeroFigure label={z.label} value={pct(z.pct)} color={z.color} swatch={z.swatch} hint={z.hint} />
            </Box>
          ))}
        </Box>
        <Grid container spacing={2} sx={{ my: 0.5, display: { md: 'none' } }}>
          {heroZones.map((z) => (
            <Grid item xs={6} key={z.label}>
              <HeroFigure label={z.label} value={pct(z.pct)} color={z.color} swatch={z.swatch} hint={z.hint} />
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 1.5 }} />
        <CoverageSpectrum segments={heroSpectrum} />
        </>
        ) : (
          <Alert severity="info" icon={false} sx={{ mt: 1.5 }}>
            <strong>Suggested replies are off</strong> for this operator — the AI auto-sends high-confidence
            replies, but staff never see the drafts, so reuse can’t be measured. Auto-send volume and
            confidence below still apply.
          </Alert>
        )}
      </Paper>

      {/* Volume cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Suggestions generated" value={num(usage.suggestions_generated)}
                    sub={ch === 'all'
                      ? `across ${num(usage.conversations_with_suggestion)} conversations`
                      : `${CHANNEL_LABEL[ch]} · ${num(usage.shown_to_staff)} provided to staff`}
                    hint={`${num(usage.shown_to_staff)} provided to staff. "Conversations" is the count of unique conversations that got at least one AI suggested reply in the window (operator-wide; not split by channel).`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Auto-sent by AI" value={num(usage.auto_sent)}
                    sub={ch === 'all'
                      ? `${pct(usage.auto_send_rate)} of suggestions · ${num(usage.conversations_with_autosent)} conversations`
                      : `${pct(usage.auto_send_rate)} of ${CHANNEL_LABEL[ch]} suggestions`}
                    hint="Replies the AI sent itself at/above the confidence threshold (excluded from reuse scoring). The conversation count is the unique conversations that got at least one auto-sent reply in the window." />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {reuseTracked ? (
          <StatCard label="Replies scored" value={num(adoption.pairs_scored)}
                    sub={m.usage.language_mismatch ? `${num(m.usage.language_mismatch)} cross-lang excl.` : undefined}
                    hint={`Manual replies paired to a preceding suggestion and scored for reuse.${
                      m.usage.language_mismatch
                        ? ` ${pct(m.usage.language_mismatch_rate)} of paired replies were in a different language than the AI suggestion and excluded from coverage (a high value flags an AI language-selection issue).`
                        : ''}`} />
          ) : (
          <StatCard label="Below threshold" value={num(usage.dropped)}
                    sub="computed, not surfaced"
                    hint="Replies the AI generated but didn't auto-send (below the confidence threshold). With suggested replies off, staff never see these." />
          )}
        </Grid>
        {/* With suggested replies on: the share of AI replies that went out as-is or with light
            edits (the bar's Auto-sent + Ready to send). Auto-send-only operators would always
            read 100% there, so they keep "AI had an answer" (a real answer vs "let me check"). */}
        <Grid item xs={12} sm={6} md={3}>
          {reuseTracked && heroTotal > 0 ? (
          <StatCard label="AI replies sent as-is or with light edits"
                    // The sum of the two figures AS SHOWN over the bar, so the card always equals
                    // "Auto-sent by AI" + "Ready to send" (rounding the exact total could be 1% off).
                    value={`${Math.round(heroAutoPct * 100) + Math.round((adoption.mostly_used_pct || 0) * heroScale * 100)}%`}
                    sub={`of ${num(heroTotal)} AI replies`}
                    hint={<TipParagraphs paragraphs={[
                      'Replies the AI sent on its own, plus AI drafts your team sent with light edits or none (they kept at least 85% of the draft), as a share of all AI replies.',
                      'The same two groups as “Auto-sent by AI” and “Ready to send” on the reply usage bar above.',
                    ]} />} />
          ) : (
          <StatCard label="AI had an answer"
                    value={usage.suggestions_generated ? pct(1 - (usage.deferral_rate || 0)) : '—'}
                    sub={usage.suggestions_generated
                      ? `${pct(usage.deferral_rate)} of questions it passed to your team`
                      : 'No questions in this period'}
                    hint={<TipParagraphs paragraphs={[
                      'How often the AI came up with a real answer to a guest’s question, instead of saying it would check with your team.',
                      'A lower figure usually means the knowledge base is missing something guests ask about.',
                    ]} />} />
          )}
        </Grid>
      </Grid>

      {/* Guest messages: conversations, share outside office hours (with the AI's own after-hours
          share) and conversations that needed no one from the team. Live data only, like the
          time saved panel (`m` is sample data while the report loads). */}
      {(liveMode || DATA_SOURCE === 'mock') && m.inbound && (
        <GuestInboundCards
          block={ch === 'all' ? m.inbound : m.inbound.channels && m.inbound.channels[ch]}
          basis={m.inbound.basis}
          channel={ch}
          officeHours={m.inbound.office_hours}
          timezone={m.inbound.timezone}
          containment={ch !== 'all' ? (m.containment && m.containment.channels && m.containment.channels[ch]) : m.containment}
          aiAfterHoursPct={ch === 'all' && m.time_saved && m.time_saved.auto_replies > 0 ? m.time_saved.after_hours_pct : null}
        />
      )}
      {(liveMode || DATA_SOURCE === 'mock') && ch === 'web_widget' && m.inbound && m.inbound.channels && m.inbound.channels.web_widget && (
        <WebChatOutcomes outcomes={m.inbound.channels.web_widget.outcomes} />
      )}

      {/* Reply kind: how the AI replies were produced (instant answer / trigger reply /
          AI-generated). Needs the `channels` block; one bar per channel for widget
          operators, a single operator-wide bar otherwise. */}
      {m.channels && (
        <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2, mt: 1, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>How the AI answered</Typography>
            <InfoTip
              title={<TipParagraphs paragraphs={[
                'AI Quick Response: the chatbot recognised one of your common questions and gave its saved answer instantly.',
                'AI Reply Rule: the chatbot recognised what the guest was asking and used the reply you configured for it.',
                'AI Insight Engine: the chatbot wrote an answer from your knowledge base.',
                'All three are replies from your AI chatbot.',
              ]} />}
              sx={{ fontSize: 15, color: 'text.disabled' }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Share of suggestions generated, by reply kind
          </Typography>
          {channelsOn || ch !== 'all'
            ? (ch === 'all' ? ['support', 'web_widget'] : [ch]).map((key) => (
              <ReplyKindBar key={key} title={CHANNEL_LABEL[key]} block={m.channels[key]} />
            ))
            : <ReplyKindBar title="All replies" block={m.channels.support} />}
        </Paper>
      )}

      {/* Trends over time */}
      <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2, mt: 1, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>Trends over time</Typography>
        <Typography variant="caption" color="text.secondary">Daily · {range.preset ? PRESET_LABEL[range.preset] : `${range.from} → ${range.to}`}</Typography>
        <Divider sx={{ my: 1.5 }} />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {ch === 'all'
                ? 'Suggestions per day (auto-sent · provided to staff · below threshold)'
                : `${CHANNEL_LABEL[ch]} suggestions per day (auto-sent · not auto-sent)`}
            </Typography>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyView} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <RechartsTooltip />
                  <Legend />
                  {/* dropped = below the auto-send threshold with suggested replies off:
                      computed but never surfaced (grey, sits at the bottom of the stack).
                      Per-channel days only record generated + auto-sent, so the channel
                      view collapses shown + dropped into one "not auto-sent" bar. */}
                  {ch === 'all' && <Bar dataKey="dropped" stackId="s" name="Below threshold (not surfaced)" fill="#cfd8dc" />}
                  {ch === 'all' && <Bar dataKey="shown" stackId="s" name="Provided to staff" fill="#90caf9" />}
                  {ch !== 'all' && <Bar dataKey="not_auto" stackId="s" name="Not auto-sent (provided to staff or below threshold)" fill="#90caf9" />}
                  <Bar dataKey="auto_sent" stackId="s" name="Auto-sent" fill="#66bb6a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {reuseTracked && (
          <>
          <Grid item xs={12} md={6}>
            <Typography variant="body2">Suggestion usage mix (% of replies)</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Greens = how much of the suggestion was used · blank space above = &lt;40% used (rewritten)
            </Typography>
            <Box sx={{ height: 222 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                  <RechartsTooltip formatter={(v) => `${v}%`} />
                  <Legend />
                  <Area type="monotone" dataKey="near" stackId="c" name="≥95%" stroke={TREND_BAND.near} fill={TREND_BAND.near} fillOpacity={0.9} />
                  <Area type="monotone" dataKey="light" stackId="c" name="85–95%" stroke={TREND_BAND.light} fill={TREND_BAND.light} fillOpacity={0.9} />
                  <Area type="monotone" dataKey="trimmed" stackId="c" name="Used, shortened" stroke={TREND_BAND.trimmed} fill={TREND_BAND.trimmed} fillOpacity={0.9} />
                  <Area type="monotone" dataKey="moderate" stackId="c" name="65–85%" stroke={TREND_BAND.moderate} fill={TREND_BAND.moderate} fillOpacity={0.9} />
                  <Area type="monotone" dataKey="heavy" stackId="c" name="40–65%" stroke={TREND_BAND.heavy} fill={TREND_BAND.heavy} fillOpacity={0.9} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          </>
          )}
          {/* Deferral rate stays visible for ALL operators — a deferral is a property of
              the reply ("let me check with my team"), so auto-sent replies can defer too. */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>AI deferral rate (%)</Typography>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 'auto']} unit="%" />
                  <RechartsTooltip formatter={(v) => `${v}%`} />
                  <Line type="monotone" dataKey="deferral" name="Deferral rate" stroke={BAND.ai_deferred} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/* Confidence trend stays visible for ALL operators (auto-send only too) —
              it's the AI's self-grade per suggestion, recorded at generation time. */}
          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>AI confidence (median)</Typography>
            <Box sx={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="confLineGrad" x1="0" y1="0" x2="0" y2="1">
                      {confStops.map((s, i) => (
                        <stop key={i} offset={`${s.offset}%`} stopColor={s.color} />
                      ))}
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 1]} />
                  <RechartsTooltip />
                  <Line type="monotone" dataKey="confidence" name="Median confidence" stroke={confStroke} strokeWidth={2.5} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
          {/* Off-operators only: the confidence DISTRIBUTION fills the otherwise-empty 4th
              slot (right of the median trend) for a clean 2x2. Inline (no nested card) to
              match the other trend charts. On-operators get the coverage calibration below. */}
          {!reuseTracked && (
          <Grid item xs={12} md={6}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
              <Typography variant="body2">AI confidence distribution</Typography>
              <InfoTip title="How many of the AI's replies fell in each self-graded confidence band — the distribution that drives auto-send at the threshold. Not compared to reuse (suggested replies are off)." sx={{ fontSize: 14, color: 'text.disabled' }} />
            </Stack>
            {(() => {
              const dist = m.confidence.distribution || [];
              const maxN = Math.max(1, ...dist.map((c) => c.n));
              return dist.map((c) => (
                <PctBar key={c.band} label={`Confidence ${c.band}`} value={c.n / maxN}
                        right={`n=${num(c.n)}`} hexColor={confidenceFill(c.conf)} />
              ));
            })()}
          </Grid>
          )}
        </Grid>
      </Paper>

      {reuseTracked && (
      <Grid container spacing={2}>
        {/* Why staff overrode (decomposition of the <40% band) */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Why staff didn’t use the suggestion
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Breakdown of the &lt;40% band. Low reuse ≠ AI error — staff may reword or add policy; run the override audit to size the truly-wrong share.
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {m.adoption.override_reasons.map((r) => (
              <PctBar key={r.key} label={r.label} value={r.pct} hexColor={r.color} />
            ))}
          </Paper>
        </Grid>

        {/* Confidence calibration: does self-confidence predict reuse? */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2, height: '100%' }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                AI confidence compared to suggested reply usage
              </Typography>
              <InfoTip title="Mean % of the suggestion used, by the AI's self-graded confidence band. If it rises with confidence, the auto-send threshold is well-calibrated; if it's flat, confidence isn't a reliable gate." sx={{ fontSize: 15, color: 'text.disabled' }} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Mean % of the suggestion used, by confidence band · median confidence {m.confidence.median}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            {/* Bars are colored by confidence band (same green→red ramp as the
                chat "AI Suggested Reply" chip); length = mean coverage. The signal
                is the descending shape — coverage tracking confidence. */}
            {m.confidence.calibration.map((c) => (
              <PctBar
                key={c.band}
                label={`Confidence ${c.band}`}
                value={c.mean_coverage}
                right={`${pct(c.mean_coverage)} used · n=${num(c.n)}`}
                hexColor={confidenceFill(c.conf)}
              />
            ))}
          </Paper>
        </Grid>

        {/* Per-agent table */}
        <Grid item xs={12} sx={{ mt: { xs: 1, md: 3 } }}>
          <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
              Per-agent suggestion usage
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {hasOutcomes
                ? 'What each agent did with the AI’s suggested replies, and the time that saved them.'
                : 'How much of the AI’s suggested replies each agent kept.'} Only replies
              where the AI gave a real answer count here. When the AI said it would check with the team, or the
              agent sent a standard operations message, there was nothing to reuse, so those replies are shown
              in their own column instead.
            </Typography>
            {hasOutcomes && (
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {OUTCOME_GROUPS.map((o) => (
                <Stack key={o.key} direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: o.color, border: '1px solid #c5d6c7' }} />
                  <Typography variant="caption" color="text.secondary">{o.label}</Typography>
                </Stack>
              ))}
            </Stack>
            )}
            <Divider sx={{ my: 1.5 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {showTrend && <TableCell sx={{ width: '1%', pl: 1, pr: 0 }} />}
                    <TableCell sx={{ pl: 0.5 }}>Agent</TableCell>
                    <TableCell align="center" sx={{ width: '12%', whiteSpace: 'nowrap' }}>
                      <ColHead label="Replies" tip={"Replies this agent sent after the AI suggested a real answer. Replies after the AI said it would check with the team, or standard operations messages, are left out."} />
                    </TableCell>
                    {hasOutcomes ? (
                    <>
                    <TableCell align="center" sx={{ width: '26%', whiteSpace: 'nowrap' }}>
                      <ColHead label="AI reply usage" tip={<TipParagraphs paragraphs={[
                        'What this agent did with the AI’s suggested replies, as a share of their replies.',
                        'Ready to send: they kept at least 85% of the draft. Strong starting point: they kept 40–85%, or sent mostly the AI’s words cut shorter. Staff reworded: they kept under 40% and wrote their own reply.',
                        'Same groups and colours as the reply usage bar at the top of the page.',
                      ]} />} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: '13%', whiteSpace: 'nowrap' }}>
                      <ColHead label="Time saved" tip={<TipParagraphs paragraphs={[
                        'Estimated time the AI’s drafts saved this agent over the selected dates.',
                        '1.5 min for each draft sent nearly as-is, 1 min with moderate edits, 0.5 min when they kept only part of it. Drafts they rewrote count for nothing.',
                      ]} />} />
                    </TableCell>
                    </>
                    ) : (
                    <>
                    <TableCell align="center" sx={{ width: '26%', whiteSpace: 'nowrap' }}>
                      <ColHead label="Draft kept" tip={<TipParagraphs paragraphs={REUSE_TIPS.draftKept} />} />
                    </TableCell>
                    <TableCell align="center" sx={{ width: '13%', whiteSpace: 'nowrap' }}>
                      <ColHead label="AI wording" tip={<TipParagraphs paragraphs={REUSE_TIPS.aiWording} />} />
                    </TableCell>
                    </>
                    )}
                    <TableCell align="center" sx={{ width: '15%', whiteSpace: 'nowrap' }}>
                      <ColHead label="AI deferrals to staff" tip={<TipParagraphs paragraphs={[
                        'How often the AI said it would check with the team instead of answering, as a share of this agent’s suggestions.',
                        `${pct(DEFER_FLAG)} or more is shown in bold: the AI often couldn’t answer this agent’s questions, which usually means its knowledge base is missing something they get asked about.`,
                      ]} />} />
                    </TableCell>
                    {showTrend && <TableCell align="center" sx={{ width: 150 }}>Draft kept trend</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {agentsView.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>
                        No agent replies to AI suggestions in {CHANNEL_LABEL[ch] || 'this channel'} for these dates.
                      </TableCell>
                    </TableRow>
                  )}
                  {agentsView.map((a) => {
                    const open = showTrend && expanded.has(a.id);
                    const nb = { borderBottom: open ? 'none' : undefined };
                    return (
                      <React.Fragment key={a.id}>
                        <TableRow
                          hover
                          sx={{ cursor: showTrend ? 'pointer' : 'default' }}
                          onClick={showTrend ? () => toggleAgent(a.id) : undefined}
                        >
                          {showTrend && (
                            <TableCell sx={{ ...nb, width: '1%', pl: 1, pr: 0 }}>
                              <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
                                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </TableCell>
                          )}
                          <TableCell sx={{ ...nb, pl: 0.5 }}>{a.name}</TableCell>
                          <TableCell align="center" sx={nb}><NumBlock width={40}>{num(a.replies)}</NumBlock></TableCell>
                          {hasOutcomes ? (
                          <>
                          <TableCell align="center" sx={nb}>
                            {a.segments ? <OutcomeBar segments={a.segments} /> : '—'}
                          </TableCell>
                          <TableCell align="center" sx={nb}>
                            <NumBlock width={44} sx={{ fontWeight: 600 }}>
                              {a.segments ? `${hoursText(agentMinutesSaved(a.segments))} h` : '—'}
                            </NumBlock>
                          </TableCell>
                          </>
                          ) : (
                          <>
                          <TableCell align="center" sx={nb}>
                            <Chip size="small" variant="outlined" label={pct(a.cov_median)}
                                  sx={{ color: usageHex(a.cov_median), borderColor: usageHex(a.cov_median), fontWeight: 600 }} />
                          </TableCell>
                          <TableCell align="center" sx={nb}>
                            <NumBlock width={34} color="text.secondary">{pct(a.prec_mean)}</NumBlock>
                          </TableCell>
                          </>
                          )}
                          <TableCell align="center" sx={nb}>
                            <NumBlock width={34} color={a.defer_pct >= DEFER_FLAG ? 'text.primary' : 'text.secondary'}
                                      sx={{ fontWeight: a.defer_pct >= DEFER_FLAG ? 600 : 400 }}>
                              {pct(a.defer_pct)}
                            </NumBlock>
                          </TableCell>
                          {showTrend && (
                            <TableCell sx={nb}>
                              <Box sx={{ width: 130, height: 30, mx: 'auto' }}>
                                <ResponsiveContainer width="100%" height="100%">
                                  <LineChart data={agentDaily(a)} margin={{ top: 4, right: 2, left: 2, bottom: 0 }}>
                                    <Line type="monotone" dataKey="coverage" stroke={sparkHex(a.cov_median)} strokeWidth={1.5} dot={false} isAnimationActive={false} />
                                  </LineChart>
                                </ResponsiveContainer>
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                        {showTrend && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ py: 0 }}>
                              <Collapse in={open} timeout="auto" unmountOnExit>
                                <Box sx={{ py: 2 }}>
                                  <Typography variant="body2" sx={{ mb: 0.25 }}>
                                    {a.name}: daily replies and draft kept
                                  </Typography>
                                  {hasOutcomes && (
                                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                                    Draft kept {pct(a.cov_median)} · AI wording {pct(a.prec_mean)}
                                  </Typography>
                                  )}
                                  <Box sx={{ height: 260 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                      <ComposedChart data={agentDaily(a)} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                                        <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                                        <YAxis yAxisId="right" orientation="right" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                                        <RechartsTooltip />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="replies" name="Replies" fill="#90caf9" radius={[4, 4, 0, 0]} />
                                        <Line yAxisId="right" type="monotone" dataKey="coverage" name="% of draft kept" stroke="#2e7d32" strokeWidth={2} dot={false} />
                                      </ComposedChart>
                                    </ResponsiveContainer>
                                  </Box>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
      )}

      {/* SEPARATE feature: notifications-module auto-replies (keyword/menu), NOT the LLM
          suggested reply. Split by whether the matched option used AI. */}
      {m.notifications && (m.notifications.sent_total > 0 || m.notifications.conversations_total > 0) && (
        <Paper variant="outlined" sx={{ borderRadius: '12px', p: 2, mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.25 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Notification auto-replies</Typography>
            <InfoTip title="The notifications-module keyword/menu auto-replies (Notification Auto-Response) — a separate feature from the AI suggested replies above. 'AI' = the matched option used a ChatGPT prompt; 'non-AI' = a literal keyword match." sx={{ fontSize: 15, color: 'text.disabled' }} />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Keyword / menu auto-replies — a separate feature from the AI suggested replies above
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <StatCard label="Auto-replies sent" value={num(m.notifications.sent_total)}
                        hint="Total notification auto-replies sent in the window. 'Untyped' were sent before AI/non-AI tracking shipped (counted in the total only)." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StatCard label="Conversations reached" value={num(m.notifications.conversations_total)}
                        hint="Unique conversations that got at least one notification auto-reply in the window (deduped across days). AI/non-AI are the conversations that got that type at least once." />
            </Grid>
          </Grid>
        </Paper>
      )}
      </>
      )}
    </Box>
  );
}
